"use client";

import { useState, useEffect, useMemo } from "react";
import { getTradesmanAds } from "@/app/actions/tradesman-ad";
import { getTradesmanCategories } from "@/app/actions/tradesman-categories";
import { BusinessCard } from "@/components/tradesman/business-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Filter, Search, Plus, Briefcase, X, MapPin, Wrench } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { ServicesTour, isServicesTourCompleted, restartServicesTour } from "@/components/tour/services-tour";
import { TourIndicator } from "@/components/tour/tour-indicator";
import { TradesmenLogo } from "@/components/tradesman/tradesmen-logo";
import { LoadingSkeleton } from "./loading-skeleton";
import { EmptyState } from "./empty-state";

interface TradesmanAd {
  id: string;
  businessName: string;
  logo?: string | null;
  phone: string;
  email: string;
  tagline?: string | null;
  servicesDescription?: string | null;
  serviceArea?: string | null;
  website?: string | null;
  city?: string | null;
  state?: string | null;
  planType: string;
  category: {
    displayName: string;
    icon?: string | null;
  };
}

interface TradesmanCategory {
  id: string;
  name: string;
  displayName: string;
  _count: {
    ads: number;
  };
}

export default function ServicesPage() {
  const [ads, setAds] = useState<TradesmanAd[]>([]);
  const [categories, setCategories] = useState<TradesmanCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [shouldRunTour, setShouldRunTour] = useState(false);
  const [showIndicator, setShowIndicator] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: session } = useSession();

  // Get city from URL parameter on mount
  useEffect(() => {
    const cityParam = searchParams.get("city");
    if (cityParam) {
      setCityFilter(cityParam);
    }
  }, [searchParams]);

  // Auto-filter by user location on mount
  useEffect(() => {
    async function loadUserLocation() {
      try {
        const { getUserGeolocation } = await import("@/lib/geolocation");
        const userLocation = await getUserGeolocation();

        if (userLocation && userLocation.city) {
          setUserCity(userLocation.city);
          setCityFilter(userLocation.city);
        }
      } catch (error) {
        // Silent fallback - no console errors
      }
    }

    loadUserLocation();
  }, []);

  // Alphabet for tabs
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const handleListYourServices = () => {
    if (!session) {
      // Redirect to log in if not logged in
      router.push('/auth/signin?callbackUrl=/user/services');
    } else {
      // Redirect to user services management page
      router.push('/user/services');
    }
  };

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const [adsResult, categoriesResult] = await Promise.all([
        getTradesmanAds({ limit: 100 }),
        getTradesmanCategories()
      ]);

      if (adsResult.success) setAds(adsResult.data as TradesmanAd[]);
      if (categoriesResult.success) setCategories(categoriesResult.data as TradesmanCategory[]);
      setLoading(false);
    }
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      const tourCompleted = isServicesTourCompleted();

      if (!tourCompleted) {
        const timer = setTimeout(() => {
          setShouldRunTour(true);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        const indicatorTimer = setTimeout(() => {
          setShowIndicator(true);
        }, 2000);
        return () => clearTimeout(indicatorTimer);
      }
    }
  }, [loading]);

  const handleRestartTour = () => {
    setShowIndicator(false);
    restartServicesTour();
  };

  // Filter ads based on selected category, search query, letter, and city
  const filteredAds = useMemo(() => {
    let filtered = ads;

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((ad) => ad.category.displayName === selectedCategory);
    }

    // Filter by city
    if (cityFilter) {
      const cityLower = cityFilter.toLowerCase();
      filtered = filtered.filter((ad) =>
        ad.city?.toLowerCase().includes(cityLower) ||
        ad.serviceArea?.toLowerCase().includes(cityLower)
      );
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (ad) =>
          ad.businessName.toLowerCase().includes(query) ||
          ad.servicesDescription?.toLowerCase().includes(query) ||
          ad.category.displayName.toLowerCase().includes(query)
      );
    }

    // Filter by alphabet letter
    if (selectedLetter) {
      filtered = filtered.filter((ad) =>
        ad.businessName.toUpperCase().startsWith(selectedLetter)
      );
    }

    return filtered;
  }, [ads, selectedCategory, searchQuery, selectedLetter, cityFilter]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-800 via-emerald-700 to-emerald-800 dark:from-emerald-950 dark:via-emerald-900 dark:to-emerald-950 text-white py-16 shadow-xl dark:shadow-emerald-950/50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-6">
            {/* Logo */}
            <div className="mb-6">
              <div className="w-32 h-32 text-white">
                <TradesmenLogo className="w-full h-full" />
              </div>
            </div>
            {/* Title and Button Row */}
            <div className="w-full flex justify-between items-start mb-4 gap-4">
              <div className="flex-1"></div>
              <div className="flex-1 flex justify-center">
                <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center tracking-tight">
                  Commercial Services Directory
                </h1>
              </div>
              <div className="flex-1 flex justify-end">
                <Button
                  onClick={handleListYourServices}
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-lg sm:h-11 h-10"
                >
                  <Plus className="mr-1.5 sm:mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">List Services</span>
                  <span className="sm:hidden">List</span>
                </Button>
              </div>
            </div>
          </div>
          <p className="text-lg text-center text-emerald-100 dark:text-emerald-200 max-w-3xl mx-auto leading-relaxed">
            Your trusted network of vetted professionals for commercial real estate. Find contractors, inspectors, architects, and service providers in your area.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* City Filter Badge */}
        {(cityFilter || userCity) && (
          <div className="mb-4">
            <Badge variant="secondary" className="text-sm px-3 py-1.5 bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-900 dark:border-emerald-700 dark:text-emerald-100">
              <MapPin className="w-4 h-4 mr-1.5 inline" />
              Showing services in: {cityFilter || userCity}
              {cityFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => setCityFilter(null)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </Badge>
          </div>
        )}

        {/* Categories Filter */}
        <div className="mb-8" data-tour="category-filters">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-slate-700 dark:text-slate-300" />
            <h2 className="font-serif text-xl font-semibold text-slate-900 dark:text-slate-100">
              Browse by Category
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              className={
                selectedCategory === null
                  ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-md dark:bg-emerald-700 dark:hover:bg-emerald-800"
                  : "bg-white border-slate-300 hover:bg-slate-50 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-200"
              }
              onClick={() => setSelectedCategory(null)}
            >
              All Categories ({ads.length})
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.displayName ? "default" : "outline"}
                className={
                  selectedCategory === category.displayName
                    ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-md dark:bg-emerald-700 dark:hover:bg-emerald-800"
                    : "bg-white border-slate-300 hover:bg-slate-50 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-200"
                }
                onClick={() => setSelectedCategory(category.displayName)}
              >
                {category.displayName} ({category._count.ads})
              </Button>
            ))}
          </div>
        </div>

        {/* Alphabet Tabs */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="font-serif text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">A-Z Filter</span>
          </div>
          <div className="flex flex-wrap gap-1">
            <Button
              variant={selectedLetter === null ? "default" : "ghost"}
              size="sm"
              className={
                selectedLetter === null
                  ? "bg-emerald-800 text-white hover:bg-emerald-900 h-11 px-3 font-medium dark:bg-emerald-700 dark:hover:bg-emerald-600"
                  : "text-slate-700 hover:bg-slate-100 h-11 px-3 dark:text-slate-300 dark:hover:bg-slate-800"
              }
              onClick={() => setSelectedLetter(null)}
            >
              All
            </Button>
            {alphabet.map((letter) => {
              const hasAds = ads.some((ad) =>
                ad.businessName.toUpperCase().startsWith(letter)
              );
              return (
                <Button
                  key={letter}
                  variant={selectedLetter === letter ? "default" : "ghost"}
                  size="sm"
                  disabled={!hasAds}
                  className={
                    selectedLetter === letter
                      ? "bg-emerald-800 text-white hover:bg-emerald-900 h-11 w-11 p-0 font-semibold dark:bg-emerald-700 dark:hover:bg-emerald-600"
                      : hasAds
                      ? "text-slate-700 hover:bg-slate-100 h-11 w-11 p-0 font-medium dark:text-slate-300 dark:hover:bg-slate-800"
                      : "text-slate-300 h-11 w-11 p-0 cursor-not-allowed dark:text-slate-600"
                  }
                  onClick={() => setSelectedLetter(letter)}
                >
                  {letter}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-8 max-w-full sm:max-w-2xl mx-auto px-0 sm:px-0">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search by business name or service..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 focus:outline-none transition-all shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-emerald-600 dark:focus:ring-emerald-900/30"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mb-8 text-center">
          <p className="text-slate-600 dark:text-slate-400 text-lg">
            Showing{" "}
            <span className="font-bold text-slate-900 dark:text-slate-100">{filteredAds.length}</span> of{" "}
            <span className="font-bold text-slate-900 dark:text-slate-100">{ads.length}</span> trusted professionals
          </p>
        </div>

        {/* Directory Grid */}
        {filteredAds.length === 0 ? (
          <EmptyState
            hasFilters={!!(searchQuery || selectedCategory || selectedLetter || cityFilter)}
            cityFilter={cityFilter}
            onClearFilters={() => {
              setSearchQuery("");
              setSelectedCategory(null);
              setSelectedLetter(null);
              setCityFilter(null);
            }}
            featuredAds={ads.filter(ad =>
              ad.planType === "FEATURED" || ad.planType === "PREMIUM"
            ).slice(0, 3)}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-tour="tradesman-cards">
            {filteredAds.map((ad) => (
              <BusinessCard key={ad.id} ad={ad} />
            ))}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-800 dark:from-emerald-900 dark:to-emerald-950 border-t-4 border-emerald-900 dark:border-emerald-950 py-12 sm:py-16 mt-16 shadow-2xl dark:shadow-emerald-950/50">
        <div className="container mx-auto px-4 text-center">
          <Briefcase className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 text-white" />
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
            Offer professional services?
          </h2>
          <p className="text-emerald-100 dark:text-emerald-200 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
            Join our directory and connect with property owners looking for your services.
            Share photos, post daily updates, and showcase your work!
          </p>
          <Button
            onClick={handleListYourServices}
            size="lg"
            className="bg-white text-emerald-800 hover:bg-emerald-50 font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all dark:bg-slate-100 dark:text-emerald-900 dark:hover:bg-white h-11 sm:h-auto"
          >
            <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">List Your Services</span>
            <span className="sm:hidden">List Services</span>
          </Button>
        </div>
      </div>

      {/* Services Tour */}
      <ServicesTour
        shouldRun={shouldRunTour}
        onComplete={() => setShouldRunTour(false)}
      />
      {showIndicator && (
        <TourIndicator
          tourName="Services Tour"
          description="Discover tradesman categories, browse business cards, and connect with professionals"
          onRestart={handleRestartTour}
          position="bottom-right"
          icon="sparkles"
        />
      )}
    </div>
  );
}
